import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

/**
 * <bbq-compare> — "Compare similar items": this product beside its related products, compared on
 * the specification attributes they actually share.
 *
 * WHY IT PARSES SPEC TABLES INSTEAD OF READING METAFIELDS
 * This catalogue has no structured spec metafields - that backfill was explicitly deferred by the
 * merchant - but 1,765 of 2,555 products (69%) carry a spec <table> inside their description, and
 * within a product type those tables share keys: measured across 40 Outdoor Grills, Model appears
 * in 28, Fuel and Construction in 23 each, Warranty and Cut-out dimensions in 20. Intersecting
 * those keys across a product and its neighbours produces a real comparison from data that is
 * already there.
 *
 * The current product's table is read from the DOM - bbq-description has already rendered it, so
 * comparing against yourself costs no request. Only the related products are fetched.
 *
 * LAZY BY DESIGN. Comparing means fetching up to three full product pages, which is far too much
 * to spend on a module most visitors never scroll to. Nothing is requested until the block is
 * near the viewport.
 *
 * DEGRADES IN TWO STEPS, because a table with one row is worse than no table:
 *   - no related products -> the block removes itself;
 *   - related products but no shared spec keys -> falls back to Price and Brand, which every
 *     product has.
 *
 * PRICES ARE MINOR UNITS FORMATTED WITH THE SHOP'S OWN MONEY FORMAT. The block emits the current
 * variant's price in cents and `shop.money_format`; the Ajax API returns cents for neighbours. Both
 * go through the theme's `formatMoney`, so the column matches what the price component prints,
 * whatever the currency's symbol position or precision.
 *
 * THE CURRENT COLUMN FOLLOWS THE VARIANT PICKER: `StandardEvents.productSelect` on this block's
 * section carries the resolved variant, and its price replaces the one rendered at load.
 */

const MAX_ROWS = 8;
const MAX_ITEMS = 3;

class BbqCompare extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  #loaded = false;

  /** The current product's cell in the Price row, once the table is rendered. */
  /** @type {HTMLTableCellElement | null} */
  #currentPriceCell = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // The block sits in bbq_specs, not in main where the picker lives: listen on the section that holds this
    // product's picker (found by product id, quick-add dialogs and cards excluded), else on its own section.
    const id = this.dataset.productId;
    const picker = id
      ? [...document.querySelectorAll(`variant-picker[data-product-id="${id}"], product-form-component[data-product-id="${id}"]`)].find((e) => !e.closest('dialog, product-card'))
      : null;
    (picker?.closest('.shopify-section') ?? this.closest('.shopify-section, dialog'))?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal,
    });

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        this.#load();
      },
      { rootMargin: '400px' }
    );
    observer.observe(this);
    signal.addEventListener('abort', () => observer.disconnect());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * A variant was picked in this block's section: keep the current column's price in step.
   * The dataset is updated too, so a table rendered after the selection starts from the right value.
   * @param {Event & {promise?: Promise<any>}} event
   */
  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail } = {}) => {
        const { newProduct, productId, resource } = detail ?? {};
        if (!newProduct && productId && String(productId) !== this.dataset.productId) return;
        if (!resource || !Number.isFinite(Number(resource.price))) return;

        this.dataset.productPrice = String(resource.price);
        if (this.#currentPriceCell) this.#currentPriceCell.textContent = this.#formatPrice(resource.price) || '—';
      })
      .catch(() => {
        // An aborted or failed selection leaves the last known price in place.
      });
  };

  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const related = await this.#fetchRelated();
    if (!related.length) {
      this.remove();
      return;
    }

    const self = {
      title: this.dataset.productTitle || '',
      url: this.dataset.productUrl || '',
      vendor: this.dataset.productVendor || '',
      price: this.#formatPrice(this.dataset.productPrice),
      image: this.dataset.productImage || '',
      specs: this.#specsFromDocument(document),
    };

    const others = await Promise.all(related.map((p) => this.#hydrate(p)));
    const columns = [self, ...others.filter(Boolean)];

    this.#render(columns);
    this.hidden = false;
  }

  async #fetchRelated() {
    const id = this.dataset.productId;
    if (!id) return [];
    try {
      const base = window.Theme?.routes?.product_recommendations_url ?? '/recommendations/products';
      const res = await fetch(`${base}.json?product_id=${encodeURIComponent(id)}&intent=related&limit=${MAX_ITEMS}`, {
        signal: this.#controller?.signal,
      });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.products || []).slice(0, MAX_ITEMS);
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[bbq-compare] related fetch failed:', error);
      return [];
    }
  }

  /** Fetch one related product's page and read its spec table. */
  async #hydrate(product) {
    let specs = {};
    try {
      const res = await fetch(product.url, { signal: this.#controller?.signal });
      if (res.ok) {
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        specs = this.#specsFromDocument(doc);
      }
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      // A neighbour that will not load is a thinner table, not a broken one.
    }

    return {
      title: product.title,
      url: product.url,
      vendor: product.vendor || '',
      price: this.#formatPrice(product.price),
      image: product.featured_image || '',
      specs,
    };
  }

  /**
   * Read key/value pairs out of the description's spec table, and out of the catalog spec table (blocks/bigapple-specs.liquid, visible variant only) for pipeline-created products.
   * @param {Document} doc
   */
  #specsFromDocument(doc) {
    const specs = {};
    for (const row of doc.querySelectorAll('.bbq-desc table tr, [data-bbq-spec-table]:not([hidden]) tr')) {
      const cells = row.querySelectorAll('td, th');
      if (cells.length !== 2) continue;
      const key = cells[0].textContent.replace(/\s+/g, ' ').trim();
      const value = cells[1].textContent.replace(/\s+/g, ' ').trim();
      if (key && value && key.length < 40) specs[key.toLowerCase()] = { label: key, value };
    }
    return specs;
  }

  /**
   * Minor units in, the shop's own money format out - the same formatter the price component uses.
   * @param {unknown} cents
   */
  #formatPrice(cents) {
    const n = Number(cents);
    if (cents === '' || cents == null || !Number.isFinite(n)) return '';
    return formatMoney(n, this.dataset.moneyFormat || '${{amount}}', this.dataset.currency || 'USD');
  }

  /** @param {any[]} columns */
  #render(columns) {
    // A key qualifies if THIS product has it and at least one neighbour does too. Requiring
    // every column was the first rule and it produced a two-row table: 31% of products carry no
    // spec table at all, so with three neighbours the odds that all four have one are about a
    // third. Measured on napoleon-entice-42, whose first neighbour has no table: the intersection
    // was empty and the whole comparison fell back to Price and Brand.
    //
    // A missing cell renders as an em dash, which reads as "not stated" - honest, and far more
    // useful than hiding an attribute three of the four products do publish.
    const others = columns.slice(1);
    const shared = Object.keys(columns[0].specs).filter((key) => others.some((c) => c.specs[key]));
    const rows = shared.slice(0, MAX_ROWS);

    const table = document.createElement('table');
    table.className = 'bbq-compare__table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    headRow.appendChild(this.#th('', 'bbq-compare__corner'));
    for (const col of columns) headRow.appendChild(this.#productHeader(col));
    thead.appendChild(headRow);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    const priceRow = this.#row('Price', columns.map((c) => c.price));
    // Cell 0 is the row header; cell 1 is this product, whose price follows the variant picker.
    this.#currentPriceCell = /** @type {HTMLTableCellElement | null} */ (priceRow.children[1] ?? null);
    tbody.appendChild(priceRow);
    tbody.appendChild(this.#row('Brand', columns.map((c) => c.vendor)));
    for (const key of rows) {
      tbody.appendChild(
        this.#row(
          columns[0].specs[key].label,
          columns.map((c) => c.specs[key]?.value ?? '')
        )
      );
    }
    table.appendChild(tbody);

    this.querySelector('[ref="body"]')?.replaceChildren(table);
  }

  #th(text, className) {
    const th = document.createElement('th');
    th.scope = 'col';
    if (className) th.className = className;
    th.textContent = text;
    return th;
  }

  #productHeader(col) {
    const th = document.createElement('th');
    th.scope = 'col';
    th.className = 'bbq-compare__product';

    if (col.image) {
      const img = document.createElement('img');
      img.src = `${col.image}${col.image.includes('?') ? '&' : '?'}width=240`;
      img.width = 100;
      img.height = 100;
      img.alt = '';
      img.loading = 'lazy';
      th.appendChild(img);
    }

    const link = document.createElement('a');
    link.href = col.url;
    link.textContent = col.title;
    th.appendChild(link);

    if (col.url === this.dataset.productUrl) {
      const badge = document.createElement('span');
      badge.className = 'bbq-compare__badge';
      badge.textContent = 'This item';
      th.appendChild(badge);
    }

    return th;
  }

  #row(label, values) {
    const tr = document.createElement('tr');
    const th = document.createElement('th');
    th.scope = 'row';
    th.textContent = label;
    tr.appendChild(th);
    for (const v of values) {
      const td = document.createElement('td');
      td.textContent = v || '—';
      tr.appendChild(td);
    }
    return tr;
  }
}

if (!customElements.get('bbq-compare')) {
  customElements.define('bbq-compare', BbqCompare);
}
